import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, filter, of, take, timeout } from 'rxjs';
import { environment } from '../../environments/environment';

export type AnalyticsEventType =
  | 'page_view'
  | 'click'
  | 'focus'
  | 'scroll'
  | 'page_duration'
  | 'form_start'
  | 'form_submit'
  | 'outbound_link'
  | 'download'
  | 'media_play'
  | 'media_pause'
  | 'media_complete'
  | 'content_view';

const ANALYTICS_FLUSH_DELAY_MS = 1_500;
const ANALYTICS_RETRY_INITIAL_DELAY_MS = 5_000;
const ANALYTICS_RETRY_MAX_DELAY_MS = 30_000;

interface AnalyticsEvent {
  type: AnalyticsEventType;
  pagePath: string;
  elementKind?: string;
  elementLabel?: string;
  scrollPercent?: number;
  durationMs?: number;
  occurredAt: string;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly apiUrl = environment.apiUrl;
  private readonly events: AnalyticsEvent[] = [];
  private readonly startedForms = new WeakSet<HTMLFormElement>();
  private readonly observedContent = new WeakSet<Element>();
  private started = false;
  private trackingEnabled = false;
  private trackingEpoch = 0;
  private sessionId: string | null = null;
  private pageStartedAt: number | null = null;
  private lastScrollAt = 0;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionCreationInFlight = false;
  private isFlushing = false;
  private retryDelayMs = ANALYTICS_RETRY_INITIAL_DELAY_MS;
  private contentObserver: IntersectionObserver | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  start(enabled: boolean): void {
    if (!this.document.defaultView) {
      return;
    }

    if (!this.started) {
      this.started = true;
      this.listenForInteractions();
    }

    this.setTrackingEnabled(enabled);
  }

  get currentSessionId(): string | null {
    return this.sessionId;
  }

  private setTrackingEnabled(enabled: boolean): void {
    if (this.trackingEnabled === enabled) {
      return;
    }

    this.trackingEpoch += 1;
    this.trackingEnabled = enabled;
    if (!enabled) {
      this.events.length = 0;
      this.sessionId = null;
      this.pageStartedAt = null;
      this.retryDelayMs = ANALYTICS_RETRY_INITIAL_DELAY_MS;
      this.clearFlushTimer();
      return;
    }

    if (this.isDocumentVisible()) {
      this.pageStartedAt = Date.now();
      this.observeContent();
      this.createSession();
    }
  }

  private createSession(): void {
    if (!this.trackingEnabled || this.sessionId || this.sessionCreationInFlight || !this.isDocumentVisible()) {
      return;
    }

    const trackingEpoch = this.trackingEpoch;
    this.sessionCreationInFlight = true;
    this.http.post<{ success: boolean; session: { publicId: string } }>(`${this.apiUrl}/analytics/sessions`, {})
      .pipe(timeout(5_000), take(1), catchError(() => of(null)))
      .subscribe((response) => {
        this.sessionCreationInFlight = false;
        if (!this.trackingEnabled || trackingEpoch !== this.trackingEpoch || !response?.session?.publicId) {
          if (this.trackingEnabled && trackingEpoch !== this.trackingEpoch) {
            this.createSession();
          }
          return;
        }
        this.sessionId = response.session.publicId;
        this.enqueue('page_view');
        this.flush();
      });
  }

  private listenForInteractions(): void {
    const window = this.document.defaultView!;
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.trackingEnabled) {
          return;
        }
        this.recordPageDuration();
        this.pageStartedAt = this.isDocumentVisible() ? Date.now() : null;
        if (this.isDocumentVisible()) {
          this.enqueue('page_view');
          window.setTimeout(() => this.observeContent());
        }
      });

    this.document.addEventListener('click', (event) => this.recordClick(event), true);
    this.document.addEventListener('focusin', (event) => this.enqueue('focus', this.describeTarget(event.target)), true);
    this.document.addEventListener('input', (event) => this.recordFormStart(event), true);
    this.document.addEventListener('submit', (event) => this.enqueue('form_submit', this.describeTarget(event.target)), true);
    this.document.addEventListener('play', (event) => this.recordMediaEvent('media_play', event), true);
    this.document.addEventListener('pause', (event) => this.recordMediaEvent('media_pause', event), true);
    this.document.addEventListener('ended', (event) => this.recordMediaEvent('media_complete', event), true);
    this.document.addEventListener('scroll', (event) => this.recordScroll(event), true);
    this.document.addEventListener('visibilitychange', () => {
      if (!this.trackingEnabled) {
        return;
      }
      if (this.document.visibilityState === 'hidden') {
        this.recordPageDuration();
        this.pageStartedAt = null;
        this.clearFlushTimer();
        return;
      }

      this.pageStartedAt = Date.now();
      if (!this.sessionId) {
        this.createSession();
      } else {
        this.scheduleFlush(0);
      }
    });
    window.addEventListener('pagehide', () => {
      if (!this.trackingEnabled) {
        return;
      }
      this.recordPageDuration();
      this.pageStartedAt = null;
      this.clearFlushTimer();
    });
  }

  private recordScroll(event: Event): void {
    if (!this.trackingEnabled) {
      return;
    }
    const now = Date.now();
    if (now - this.lastScrollAt < 2_000) {
      return;
    }
    this.lastScrollAt = now;
    const rawTarget = event.target;
    const source = rawTarget instanceof Element ? rawTarget : this.document.documentElement;
    const maximum = Math.max(1, source.scrollHeight - source.clientHeight);
    this.enqueue('scroll', {
      elementKind: source === this.document.documentElement ? 'document' : source.tagName.toLowerCase(),
      scrollPercent: Math.max(0, Math.min(100, Math.round((source.scrollTop / maximum) * 100))),
    });
  }

  private describeTarget(target: EventTarget | null): Pick<AnalyticsEvent, 'elementKind' | 'elementLabel'> {
    if (!(target instanceof Element)) {
      return {};
    }
    const element = target.closest('a, button, form, input, select, textarea, [role="button"], [tabindex]');
    if (!element) {
      return {};
    }
    const kind = element.tagName.toLowerCase();
    let label = element.getAttribute('data-bw-analytics-label') || element.getAttribute('aria-label') || '';
    if (!label && kind === 'form') {
      label = element.getAttribute('name') || element.id || 'form';
    }
    if (!label && ['input', 'select', 'textarea'].includes(kind)) {
      label = `${kind}${element.getAttribute('type') ? `[${element.getAttribute('type')}]` : ''}`;
    }
    if (!label && kind !== 'form') {
      label = element.textContent || '';
    }
    return {
      elementKind: kind.slice(0, 48),
      elementLabel: label.replace(/\s+/g, ' ').trim().slice(0, 160) || undefined,
    };
  }

  private recordClick(event: Event): void {
    const target = event.target;
    if (target instanceof Element) {
      const link = target.closest('a');
      if (link) {
        if (link.hasAttribute('download')) {
          this.enqueue('download', this.describeTarget(link));
        } else {
          const destination = this.externalLink(link);
          if (destination) {
            this.enqueue('outbound_link', this.describeTarget(link));
          }
        }
      }
    }
    this.enqueue('click', this.describeTarget(target));
  }

  private recordFormStart(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const form = target.closest('form');
    if (!form || this.startedForms.has(form)) {
      return;
    }
    this.startedForms.add(form);
    this.enqueue('form_start', this.describeTarget(form));
  }

  private recordMediaEvent(type: Extract<AnalyticsEventType, 'media_play' | 'media_pause' | 'media_complete'>, event: Event): void {
    const media = event.target;
    if (!(media instanceof HTMLMediaElement)) {
      return;
    }
    this.enqueue(type, {
      elementKind: media.tagName.toLowerCase(),
      elementLabel: media.getAttribute('data-bw-analytics-label') || media.getAttribute('aria-label') || media.tagName.toLowerCase(),
    });
  }

  private observeContent(): void {
    if (!this.trackingEnabled) {
      return;
    }
    const window = this.document.defaultView;
    if (!window || !('IntersectionObserver' in window)) {
      return;
    }
    if (!this.contentObserver) {
      this.contentObserver = new window.IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || this.observedContent.has(entry.target)) {
            continue;
          }
          this.observedContent.add(entry.target);
          this.contentObserver?.unobserve(entry.target);
          this.enqueue('content_view', this.describeContent(entry.target));
        }
      }, { threshold: [0.5] });
    }

    this.document.querySelectorAll('main section, [data-bw-analytics-track]').forEach((element) => {
      if (!this.observedContent.has(element)) {
        this.contentObserver?.observe(element);
      }
    });
  }

  private describeContent(element: Element): Pick<AnalyticsEvent, 'elementKind' | 'elementLabel'> {
    return {
      elementKind: element.tagName.toLowerCase().slice(0, 48),
      elementLabel: (element.getAttribute('data-bw-analytics-label') || element.getAttribute('aria-label') || element.id || 'section')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160),
    };
  }

  private externalLink(link: HTMLAnchorElement): URL | null {
    const window = this.document.defaultView;
    if (!window || !link.href) {
      return null;
    }
    try {
      const destination = new URL(link.href, window.location.href);
      return /^https?:$/.test(destination.protocol) && destination.origin !== window.location.origin ? destination : null;
    } catch {
      return null;
    }
  }

  private recordPageDuration(): void {
    if (!this.trackingEnabled || this.pageStartedAt === null) {
      return;
    }

    const durationMs = Date.now() - this.pageStartedAt;
    if (durationMs >= 1_000) {
      this.enqueue('page_duration', { durationMs });
    }
  }

  private enqueue(type: AnalyticsEventType, detail: Partial<AnalyticsEvent> = {}): void {
    if (!this.trackingEnabled) {
      return;
    }
    this.events.push({ type, pagePath: this.pagePath(), occurredAt: new Date().toISOString(), ...detail });
    if (this.events.length >= 20) {
      this.flush();
      return;
    }
    this.scheduleFlush();
  }

  private flush(): void {
    if (!this.trackingEnabled || this.isFlushing || !this.isDocumentVisible() || !this.sessionId || !this.events.length) {
      return;
    }

    this.clearFlushTimer();
    const events = this.events.splice(0, 50);
    const trackingEpoch = this.trackingEpoch;
    this.isFlushing = true;
    this.http.post<{ success: boolean }>(`${this.apiUrl}/analytics/events`, { sessionId: this.sessionId, events })
      .pipe(timeout(5_000), take(1), catchError(() => of(null)))
      .subscribe((response) => {
        this.isFlushing = false;
        if (!this.trackingEnabled || trackingEpoch !== this.trackingEpoch) {
          return;
        }
        if (response === null) {
          this.events.unshift(...events);
          this.events.splice(50);
          this.scheduleFlush(this.retryDelayMs);
          this.retryDelayMs = Math.min(this.retryDelayMs * 2, ANALYTICS_RETRY_MAX_DELAY_MS);
          return;
        }

        this.retryDelayMs = ANALYTICS_RETRY_INITIAL_DELAY_MS;
        this.scheduleFlush(this.events.length >= 20 ? 0 : ANALYTICS_FLUSH_DELAY_MS);
      });
  }

  private scheduleFlush(delayMs = ANALYTICS_FLUSH_DELAY_MS): void {
    if (!this.trackingEnabled || this.flushTimer || !this.isDocumentVisible() || !this.sessionId || !this.events.length) {
      return;
    }

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  private clearFlushTimer(): void {
    if (!this.flushTimer) {
      return;
    }
    clearTimeout(this.flushTimer);
    this.flushTimer = null;
  }

  private isDocumentVisible(): boolean {
    return this.document.visibilityState !== 'hidden';
  }

  private pagePath(): string {
    return this.router.url.split('?')[0]?.split('#')[0] || '/';
  }
}
